import { useEffect, useState } from "react";
import { Plus, Stack, Trash, PencilSimple, TrendUp, TrendDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { TemplateEditor } from "../components/TemplateEditor";
import { api, formatMoney } from "../lib/api";

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setTemplates(await api.listTemplates());
    } catch (e) {
      toast.error("Error al cargar plantillas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEditor = (tpl) => { setEditing(tpl); setEditorOpen(true); };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`¿Eliminar la plantilla "${tpl.name}"? Los planes ya creados no se modifican.`)) return;
    try {
      await api.deleteTemplate(tpl.id);
      toast.success("Plantilla eliminada");
      load();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div>
      <div className="h-16 border-b border-border px-6 flex items-center justify-between bg-card">
        <div>
          <div className="label-xs text-muted-foreground">Reutilizables</div>
          <h1 className="font-heading text-xl font-extrabold tracking-tight">Plantillas de Plan</h1>
        </div>
        <Button data-testid="new-template-btn" onClick={() => openEditor(null)} className="rounded-sm font-semibold">
          <Plus size={16} weight="bold" className="mr-2" /> Nueva Plantilla
        </Button>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="py-12 text-center text-muted-foreground">Cargando…</div>
        ) : templates.length === 0 ? (
          <div className="border border-dashed border-border rounded-sm p-14 text-center" data-testid="templates-empty">
            <Stack size={40} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
            <h3 className="font-heading font-bold text-lg">No hay plantillas todavía</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Crea una plantilla con tus partidas recurrentes, o guarda una versión de un plan como plantilla.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
            {templates.map((t) => (
              <TemplateCard key={t.id} tpl={t} onEdit={() => openEditor(t)} onDelete={() => handleDelete(t)} />
            ))}
          </div>
        )}
      </div>

      <TemplateEditor open={editorOpen} onOpenChange={setEditorOpen} template={editing} onSaved={load} />
    </div>
  );
}

const TemplateCard = ({ tpl, onEdit, onDelete }) => {
  const income = tpl.entries.filter((e) => e.type === "income").reduce((s, e) => s + e.amount, 0);
  const expense = tpl.entries.filter((e) => e.type === "expense").reduce((s, e) => s + e.amount, 0);
  return (
    <div className="card-elevated hover:border-foreground/30 transition-colors" data-testid={`template-card-${tpl.id}`}>
      <div className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="label-xs text-muted-foreground">{tpl.entries.length} partidas</div>
            <h3 className="font-heading font-extrabold text-lg mt-1 tracking-tight">{tpl.name}</h3>
            {tpl.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{tpl.description}</p>}
          </div>
          <Stack size={20} weight="duotone" className="text-muted-foreground shrink-0" />
        </div>
        <div className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Saldo inicial sugerido</span><span className="font-mono-tabular font-semibold">{formatMoney(tpl.initial_balance)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1"><TrendUp size={12} /> Ingresos</span><span className="font-mono-tabular font-semibold text-emerald-700 dark:text-emerald-400">{formatMoney(income)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1"><TrendDown size={12} /> Egresos</span><span className="font-mono-tabular font-semibold text-red-700 dark:text-red-400">{formatMoney(expense)}</span></div>
        </div>
      </div>
      <div className="flex border-t border-border">
        <button data-testid={`edit-template-${tpl.id}`} onClick={onEdit}
          className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold hover:bg-muted transition-colors">
          <PencilSimple size={15} /> Editar
        </button>
        <button data-testid={`delete-template-${tpl.id}`} onClick={onDelete}
          className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold border-l border-border text-muted-foreground hover:text-destructive hover:bg-muted transition-colors">
          <Trash size={15} /> Eliminar
        </button>
      </div>
    </div>
  );
};
